/**
 * 图片加载失败的统一诊断。
 *
 * 用户端(web)与管理端(admin)共用同一实现，确保两端对「图片裂了」的提示口径一致：
 * 既告知「加载失败」，也给出可读的失败原因（HTTPS 混合内容升级 / CDN 证书不受信 /
 * 签名过期 / 防盗链 / 404 / 5xx 等），并附带资源域名用于定位。
 *
 * 纯函数、无副作用；仅在探测页面协议时读取 window（已做 guard），可安全用于两端。
 * 修改此文件后两端无需各自维护副本。
 */

export type ImageLoadErrorDiagnosis = {
  /** 主标题，如 "加载失败" 或 "加载失败 403" */
  title: string
  /** 面向用户/运营的可读失败原因（完整版，管理端展示） */
  reason: string
  /** 面向用户端(web)的简短原因：一行以内，完整诊断仅在管理端展示 */
  reasonShort: string
  /** 资源域名，用于定位（如 zwr.qiniucdn.ai.fromzwr.online） */
  hostLabel: string
  /** 不带查询串的安全地址片段（host + 截断 path），展示用，避免泄露签名 token */
  safeUrlLabel: string
}

function isLikelyQiniuHostname(hostname: string): boolean {
  const h = hostname.toLowerCase()
  return (
    h.includes('qiniucdn.') ||
    h.includes('qiniucdn.com') ||
    h.includes('qiniudn.com') ||
    h.includes('clouddn.com') ||
    h.includes('qbox.me')
  )
}

/** 截断路径（去掉查询串），用于失败文案，避免泄露签名 token(e/token) */
function buildSafeUrlLabel(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  try {
    const u = new URL(trimmed)
    const host = u.host
    const path = u.pathname || '/'
    const full = `${host}${path}`
    const MAX = 44
    if (full.length <= MAX) return full
    const head = Math.ceil(MAX * 0.66)
    const tail = MAX - head - 1
    return `${full.slice(0, head)}…${full.slice(-tail)}`
  } catch {
    const noQuery = trimmed.split('?')[0]
    return noQuery.length > 48 ? `${noQuery.slice(0, 34)}…` : noQuery
  }
}

/** 是否七牛私有桶的带签名下载链（同时带 e=过期时间戳 与 token=签名） */
function hasSignedDownloadToken(raw: string): boolean {
  try {
    const u = new URL(raw)
    return u.searchParams.has('e') && u.searchParams.has('token')
  } catch {
    return false
  }
}

function currentPageScheme(): 'https' | 'http' {
  try {
    if (typeof window !== 'undefined' && window.location?.protocol === 'https:') {
      return 'https'
    }
  } catch {
    /* ignore */
  }
  return 'http'
}

export function diagnoseImageLoadError(opts: {
  url: string | null | undefined
  /** 可选：探测到的 HTTP 状态码（仅同源资源可得；跨域 CDN 通常拿不到） */
  httpStatus?: number | null
}): ImageLoadErrorDiagnosis {
  const raw = (opts.url ?? '').trim()
  let host = ''
  let scheme = ''
  let isQiniu = false
  let isRemote = false
  try {
    const u = new URL(raw)
    host = u.host
    scheme = u.protocol.replace(':', '').toLowerCase()
    isQiniu = isLikelyQiniuHostname(u.hostname)
    isRemote = true
  } catch {
    /* 非绝对地址：/uploads 相对路径、空串等 */
  }

  const status =
    opts.httpStatus != null && Number.isFinite(opts.httpStatus)
      ? Math.trunc(opts.httpStatus as number)
      : null

  const safeUrlLabel = buildSafeUrlLabel(raw)
  const isSigned = hasSignedDownloadToken(raw)

  // 1) 已知 HTTP 状态码优先解释（同源资源可得）
  if (status != null) {
    let reason: string
    let reasonShort: string
    if (status === 401 || status === 403) {
      if (isSigned) {
        // 签名链还没到期就被拒：七牛返回 {"error":"download token auth failed"}，
        // 几乎总是「生成签名的 SecretKey 与 AccessKey 不配对」（填错 SK / 用了另一个账号的 SK），
        // 也可能是 AK 所属账号没有该桶权限。重签解决不了，必须改存储凭证。
        reason =
          '资源返回了 ' +
          status +
          '：带签名的下载地址被拒绝（download token auth failed）。' +
          '链接本身还没到期，通常是存储设置里的 SecretKey 与 AccessKey 不配对，或该账号没有这个桶的权限；' +
          '请到「存储设置」核对 AK/SK 与所属账号后重新刷新页面。'
        reasonShort = '存储凭证校验失败，请到存储设置核对 AK/SK'
      } else {
        reason =
          '资源返回了 ' + status + '（未授权/被拒绝），可能是签名已过期或 CDN 防盗链限制。'
        reasonShort = '签名已过期或触发防盗链，请刷新重试'
      }
    } else if (status === 404) {
      reason = '资源返回了 404，链接可能已失效或对象已被删除。'
      reasonShort = '图片不存在或已被删除'
    } else if (status >= 500) {
      reason = '资源服务器返回了 ' + status + '，暂时不可用，请稍后重试。'
      reasonShort = '图片服务暂时不可用，请稍后重试'
    } else {
      reason = '资源返回了 ' + status + ' 状态码，加载被中断。'
      reasonShort = '图片加载被中断，请重试'
    }
    return { title: '加载失败 ' + status, reason, reasonShort, hostLabel: host, safeUrlLabel }
  }

  // 2) URL 形态诊断（无状态码：混合内容升级 / 证书 / 网络不可达）
  if (isRemote) {
    if (scheme === 'http' && currentPageScheme() === 'https') {
      const reason =
        '当前页面是 HTTPS，而图片地址是 HTTP；浏览器会将其自动升级为 HTTPS 加载。' +
        (isQiniu
          ? '该七牛 CDN 自定义域名的 HTTPS 证书不受信任或已过期，导致 TLS 握手失败。请联系管理员更新证书，或改用受信的 HTTPS 域名。'
          : '若目标域名的 HTTPS 证书不受信任或已过期会在此失败。请在新标签页打开链接验证，或联系管理员。')
      // 用户端统一短文案：只告知证书不受信任，完整诊断仅在管理端展示
      const reasonShort = '图片加载失败，证书不受信任'
      return { title: '加载失败', reason, reasonShort, hostLabel: host, safeUrlLabel }
    }
    if (isQiniu) {
      const reason = isSigned
        ? '图片来自七牛 CDN（该地址带 e/token 签名）。若被云端拒绝（download token auth failed），' +
          '多为 AK/SK 不配对或账号无该桶权限；若签名已过期则会提示过期。请先在「存储设置」核对凭证再重试。'
        : '图片来自七牛 CDN。若签名已过期或触发防盗链会无法加载；请点击刷新后重试，仍失败请联系管理员。'
      const reasonShort = isSigned ? '图片签名可能已过期，请刷新重试' : '图片加载失败，请刷新重试'
      return { title: '加载失败', reason, reasonShort, hostLabel: host, safeUrlLabel }
    }
    const reason = '图片地址无法加载。请在新标签页打开链接验证，或检查网络与链接有效性。'
    const reasonShort = '图片无法加载，请检查链接有效性'
    return { title: '加载失败', reason, reasonShort, hostLabel: host, safeUrlLabel }
  }

  // 3) 站内相对路径或无法解析（通常是 /uploads 失效、服务异常）
  const reason = host
    ? '图片无法加载（域名 ' + host + '）。请刷新后重试，或联系管理员检查资源服务。'
    : '图片无法加载。请刷新后重试，或检查链接是否有效。'
  const reasonShort = '图片无法加载，请刷新重试'
  return { title: '加载失败', reason, reasonShort, hostLabel: host, safeUrlLabel }
}
